import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ShieldAlert, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/api";
import { shortTag } from "@/utils/format";

const STATUS_STYLE = {
  pending: { label: "En attente", cls: "bg-brand-yellow/20 text-[#8A6D00]" },
  approved: { label: "Validée", cls: "bg-[#E7F6EE] text-brand-green" },
  rejected: { label: "Rejetée", cls: "bg-brand-red/10 text-brand-red" },
  cancelled: { label: "Annulée", cls: "bg-elevated text-mute" },
};
const PAYOUT_FIELDS = ["payout_operator", "payout_number", "payout_holder"];

export default function ChangeRequestsPanel({ onChanged }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null); // request being rejected
  const [reason, setReason] = useState("");
  const [searchParams] = useSearchParams();
  const highlight = searchParams.get("highlight");

  const load = useCallback(() => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    apiClient.get(`/admin/change-requests${q}`).then(({ data }) => setItems(data.items || [])).catch(() => {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Deep link from the admin notification → scroll to and flash the request
  useEffect(() => {
    if (!highlight || items.length === 0) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`chg-${highlight}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("deep-link-highlight");
        setTimeout(() => el.classList.remove("deep-link-highlight"), 3200);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [highlight, items]);

  const approve = async (r) => {
    if (!confirm(`Valider la modification « ${r.field_label} » de ${r.partner_name} ?\n${r.old_value || "—"} → ${r.new_value}`)) return;
    setBusyId(r.request_id);
    try {
      await apiClient.post(`/admin/change-requests/${r.request_id}/approve`);
      toast.success(t("admin.crApprovedToast"));
      load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  const reject = async () => {
    if (reason.trim().length < 3) { toast.error(t("admin.crReasonRequired")); return; }
    setBusyId(rejecting.request_id);
    try {
      await apiClient.post(`/admin/change-requests/${rejecting.request_id}/reject`, { reason: reason.trim() });
      toast.success(t("admin.crRejectedToast"));
      setRejecting(null);
      setReason("");
      load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-change-requests">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`chg-filter-${f}`}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold border ${filter === f ? "bg-ink text-white border-ink" : "bg-white border-sand"}`}
          >
            {{ pending: t("admin.crPending"), approved: t("admin.crApprovedF"), rejected: t("admin.crRejectedF"), all: t("admin.crAll") }[f]}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-sand rounded-2xl p-10 text-center text-mute">Aucune demande.</div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => {
            const st = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
            const isPayout = PAYOUT_FIELDS.includes(r.field);
            return (
              <div key={r.request_id} id={`chg-${r.request_id}`} data-testid={`chg-row-${r.request_id}`} className="bg-white border border-sand rounded-2xl p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{r.partner_name}</span>
                      {r.partner_role === "driver" && <span className="font-mono text-xs text-mute">{shortTag(r.user_id)}</span>}
                      <span className="text-xs uppercase tracking-widest text-brand-red">{r.partner_role}</span>
                      {r.restaurant_name && <span className="text-xs text-mute">· {r.restaurant_name}</span>}
                      <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                    </div>
                    <div className="text-sm mt-1.5">
                      <span className="text-xs uppercase tracking-widest text-mute">{r.field_label}</span>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="line-through text-mute">{r.old_value || "—"}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-mute" />
                        <b>{r.new_value}</b>
                      </div>
                    </div>
                    {isPayout && r.status === "pending" && (
                      <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-brand-red/10 text-brand-red px-3 py-1 text-[11px] font-bold" data-testid={`chg-suspend-badge-${r.request_id}`}>
                        <ShieldAlert className="w-3.5 h-3.5" /> Reversements suspendus jusqu'à validation
                      </div>
                    )}
                    {r.status === "rejected" && r.reason && (
                      <div className="text-xs text-brand-red mt-1.5">Motif : {r.reason}</div>
                    )}
                    <div className="text-[11px] text-mute mt-1.5">
                      Demandé le {new Date(r.created_at).toLocaleString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      {r.decided_at && ` · traité le ${new Date(r.decided_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}`}
                    </div>
                  </div>
                  {r.status === "pending" && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => approve(r)}
                        disabled={busyId === r.request_id}
                        data-testid={`chg-approve-${r.request_id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-brand-green text-white px-4 py-2 text-xs font-semibold disabled:opacity-60"
                      >
                        {busyId === r.request_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Valider
                      </button>
                      <button
                        onClick={() => { setRejecting(r); setReason(""); }}
                        data-testid={`chg-reject-${r.request_id}`}
                        className="inline-flex items-center gap-1 rounded-full border border-brand-red text-brand-red hover:bg-brand-red hover:text-white px-4 py-2 text-xs font-semibold"
                      >
                        <XCircle className="w-3 h-3" /> Rejeter
                      </button>
                    </div>
                  )}
                </div>

                {rejecting?.request_id === r.request_id && (
                  <div className="mt-3 rounded-xl bg-elevated p-3" data-testid="chg-reject-form">
                    <label className="text-xs uppercase tracking-widest text-mute block mb-1">Motif du rejet (obligatoire, visible par le partenaire)</label>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={2}
                      data-testid="chg-reject-reason"
                      className="w-full bg-white border border-sand rounded-xl px-3 py-2 text-sm outline-none focus:border-brand-red"
                      placeholder={t("admin.crRejectPh")}
                    />
                    <div className="flex gap-2 mt-2">
                      <button onClick={reject} disabled={busyId === r.request_id} data-testid="chg-reject-confirm" className="rounded-full bg-brand-red text-white px-4 py-1.5 text-xs font-semibold disabled:opacity-60">
                        Confirmer le rejet
                      </button>
                      <button onClick={() => setRejecting(null)} className="text-xs text-mute underline">Annuler</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
